import type { AnyRecord } from "@entrilia/shared/types";
import type {
  BooleanObjectFilter,
  DateObjectFilter,
  Field,
  MultiSelectObjectFilter,
  NumberObjectFilter,
  ObjectFilter,
  TextObjectFilter,
} from "../../../api/types/objectTypes";
import type {
  BooleanFilterValue,
  DateFilterValue,
  MultiSelectFilterValue,
  NumberFilterValue,
  TableFilter,
  TextFilterValue,
} from "./filterTypes";

// Fontend filters to API object filters mapping

const createTextObjectFilter = (filterValue: TextFilterValue): TextObjectFilter | undefined => {
  switch (filterValue.operator) {
    case "empty": {
      return { operator: "Empty" };
    }
    case "not_empty": {
      return { operator: "NotEmpty" };
    }
    case "contains": {
      return filterValue.text ? { operator: "Contains", value: filterValue.text } : undefined;
    }
    case "does_not_contain": {
      return filterValue.text ? { operator: "DoesNotContain", value: filterValue.text } : undefined;
    }
    default: {
      return undefined;
    }
  }
};

const createNumberObjectFilter = (filterValue: NumberFilterValue): NumberObjectFilter | undefined => {
  switch (filterValue.operator) {
    case "empty": {
      return { operator: "Empty" };
    }
    case "not_empty": {
      return { operator: "NotEmpty" };
    }
    case "equals": {
      return filterValue.number !== undefined ? { operator: "Equals", value: filterValue.number } : undefined;
    }
    case "not_equals": {
      return filterValue.number !== undefined ? { operator: "NotEquals", value: filterValue.number } : undefined;
    }
    case "between": {
      return filterValue.number !== undefined && filterValue.secondNumber !== undefined
        ? { operator: "Between", from: filterValue.number, to: filterValue.secondNumber }
        : undefined;
    }
    case "greater_than": {
      return filterValue.number !== undefined ? { operator: "GreaterThan", from: filterValue.number } : undefined;
    }
    case "less_than": {
      return filterValue.number !== undefined ? { operator: "LessThan", to: filterValue.number } : undefined;
    }
    default: {
      return undefined;
    }
  }
};

const createDateObjectFilter = (filterValue: DateFilterValue): DateObjectFilter | undefined => {
  switch (filterValue.operator) {
    case "empty": {
      return { operator: "Empty" };
    }
    case "not_empty": {
      return { operator: "NotEmpty" };
    }
    case "range": {
      return filterValue.range !== undefined && !!filterValue.range[0] && !!filterValue.range[1]
        ? { operator: "Range", from: filterValue.range[0], to: filterValue.range[1] }
        : undefined;
    }
    default: {
      return undefined;
    }
  }
};

const createBooleanObjectFilter = (filterValue: BooleanFilterValue): BooleanObjectFilter | undefined => {
  return filterValue.operatorValue === undefined ? undefined : { value: filterValue.operatorValue };
};

const createMultiSelectObjectFilter = (filterValue: MultiSelectFilterValue): MultiSelectObjectFilter | undefined => {
  switch (filterValue.operator) {
    case "empty": {
      return { operator: "Empty" };
    }
    case "not_empty": {
      return { operator: "NotEmpty" };
    }
    case "one_of": {
      return filterValue.selectedValues !== undefined && filterValue.selectedValues.length > 0
        ? { operator: "OneOf", selectedValues: filterValue.selectedValues }
        : undefined;
    }
    case "not_one_of": {
      return filterValue.selectedValues !== undefined && filterValue.selectedValues.length > 0
        ? { operator: "NotOneOf", selectedValues: filterValue.selectedValues }
        : undefined;
    }
    default: {
      return undefined;
    }
  }
};

export const mapTableFilterToApiObjectFilter = <R extends AnyRecord>(
  filter: TableFilter<R>
): ObjectFilter | undefined => {
  const result: ObjectFilter = {
    fieldId: filter.id,
    textFilter: filter.type === "text" ? createTextObjectFilter(filter.value) : undefined,
    numberFilter: filter.type === "number" ? createNumberObjectFilter(filter.value) : undefined,
    dateFilter: filter.type === "date" ? createDateObjectFilter(filter.value) : undefined,
    booleanFilter: filter.type === "boolean" ? createBooleanObjectFilter(filter.value) : undefined,
    multiSelectFilter: filter.type === "multi_select" ? createMultiSelectObjectFilter(filter.value) : undefined,
  };

  const allFiltersEmpty = Object.keys(result)
    .filter((key) => key !== "fieldId")
    .every((key) => result[key as keyof ObjectFilter] === undefined);

  return allFiltersEmpty ? undefined : result;
};

// API object filters to frontend filters mapping

const getTextFilterValue = (objectFilter: TextObjectFilter): TextFilterValue | undefined => {
  switch (objectFilter.operator) {
    case "Empty":
      return { operator: "empty" };
    case "NotEmpty":
      return { operator: "not_empty" };
    case "Contains":
      return { operator: "contains", text: objectFilter.value };
    case "DoesNotContain":
      return { operator: "does_not_contain", text: objectFilter.value };
    default:
      return undefined;
  }
};

const getNumberFilterValue = (objectFilter: NumberObjectFilter): NumberFilterValue | undefined => {
  switch (objectFilter.operator) {
    case "Empty":
      return { operator: "empty", number: undefined };
    case "NotEmpty":
      return { operator: "not_empty", number: undefined };
    case "Equals":
      return objectFilter.value !== undefined ? { operator: "equals", number: objectFilter.value } : undefined;
    case "NotEquals":
      return objectFilter.value !== undefined ? { operator: "not_equals", number: objectFilter.value } : undefined;
    case "Between":
      return objectFilter.from !== undefined && objectFilter.to !== undefined
        ? { operator: "between", number: objectFilter.from, secondNumber: objectFilter.to }
        : undefined;
    case "GreaterThan":
      return objectFilter.from !== undefined ? { operator: "greater_than", number: objectFilter.from } : undefined;
    case "LessThan":
      return objectFilter.to !== undefined ? { operator: "less_than", number: objectFilter.to } : undefined;
    default:
      return undefined;
  }
};

const getDateFilterValue = (objectFilter: DateObjectFilter): DateFilterValue | undefined => {
  switch (objectFilter.operator) {
    case "Empty":
      return { operator: "empty" };
    case "NotEmpty":
      return { operator: "not_empty" };
    case "Range":
      return objectFilter.from && objectFilter.to
        ? { operator: "range", range: [objectFilter.from, objectFilter.to] }
        : undefined;
    default:
      return undefined;
  }
};

const getBooleanFilterValue = (objectFilter: BooleanObjectFilter): BooleanFilterValue | undefined => {
  return objectFilter.value !== undefined ? { operator: "equals", operatorValue: objectFilter.value } : undefined;
};

const getMultiSelectFilterValue = (objectFilter: MultiSelectObjectFilter): MultiSelectFilterValue | undefined => {
  switch (objectFilter.operator) {
    case "Empty":
      return { operator: "empty" };
    case "NotEmpty":
      return { operator: "not_empty" };
    case "OneOf":
      return objectFilter.selectedValues && objectFilter.selectedValues.length > 0
        ? { operator: "one_of", selectedValues: objectFilter.selectedValues }
        : undefined;
    case "NotOneOf":
      return objectFilter.selectedValues && objectFilter.selectedValues.length > 0
        ? { operator: "not_one_of", selectedValues: objectFilter.selectedValues }
        : undefined;
    default:
      return undefined;
  }
};

export const mapApiObjectFilterToReadonlyTableFilter = <R extends AnyRecord>(
  objectFilter: ObjectFilter,
  objectFields: Field[]
): TableFilter<R> | undefined => {
  const filterName = objectFields.find((field) => field.id === objectFilter.fieldId)?.name || objectFilter.fieldId;
  const filter = { id: objectFilter.fieldId, name: filterName, readOnly: true, dynamic: true };

  if (objectFilter.textFilter) {
    const value = getTextFilterValue(objectFilter.textFilter);
    return value ? { ...filter, type: "text", value } : undefined;
  }

  if (objectFilter.numberFilter) {
    const value = getNumberFilterValue(objectFilter.numberFilter);
    return value ? { ...filter, type: "number", value } : undefined;
  }

  if (objectFilter.dateFilter) {
    const value = getDateFilterValue(objectFilter.dateFilter);
    return value ? { ...filter, type: "date", value } : undefined;
  }

  if (objectFilter.booleanFilter) {
    const value = getBooleanFilterValue(objectFilter.booleanFilter);
    return value ? { ...filter, type: "boolean", value } : undefined;
  }

  if (objectFilter.multiSelectFilter) {
    const value = getMultiSelectFilterValue(objectFilter.multiSelectFilter);
    return value ? { ...filter, type: "multi_select", value } : undefined;
  }

  return undefined;
};
